/* ============================================================================
   DeskNeko 4.2 (Claude) — Mobile "calm" fixed-layer controller
   assets/dn-mobile-calm.js   ·   paired with assets/dn-mobile-calm.css
   Loaded (defer) from sections/mobile-bottom-nav.liquid (header group).

   Owns four calm mobile behaviours (all <= 991px, all reduced-motion safe):
     1. Header hides on scroll DOWN, returns on scroll UP, always shown at top.
     2. Floating filter trigger shows ONLY while the product grid is in view.
     3. Back-to-top appears only after ~50% of the scrollable page.
     4. Support chat launcher rests subtler, with a gentle occasional nudge
        (long idle dwell OR deep scroll) — veil/nudge via a body class only.

   It toggles classes that dn-mobile-calm.css consumes. It NEVER positions the
   bottom-right fixed stack (that is dnFixedLayerContract's job); after header
   toggles it merely asks the contract to recompute. Nothing here touches the
   navbar / footer / filter / chat source files.
   ============================================================================ */
(function () {
  'use strict';

  if (window.__dnMobileCalmInit) return;
  window.__dnMobileCalmInit = true;

  var MOBILE_MQ = window.matchMedia('(max-width: 991px)');
  var RM_MQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  var body = document.body;
  var docEl = document.documentElement;

  body.classList.add('dn-mobile-calm');

  var header = document.querySelector('.shopify-section-group-header-group.sticky-top') ||
    document.querySelector('.sticky-top');

  function isMobile() { return MOBILE_MQ.matches; }
  function reducedMotion() { return RM_MQ.matches; }

  function hasOpenOverlay() {
    return body.classList.contains('modal-open') ||
      !!document.querySelector('.offcanvas.show, .modal.show');
  }

  function recomputeContract() {
    try {
      var c = window.dnFixedLayerContract;
      if (c && typeof c.updateBodyReserve === 'function') c.updateBodyReserve();
    } catch (e) { /* contract not ready yet — harmless */ }
  }

  function scrollableHeight() {
    return Math.max(0, (docEl.scrollHeight || 0) - window.innerHeight);
  }
  function currentY() {
    return window.scrollY || window.pageYOffset || 0;
  }
  function scrollDepth() {
    var h = scrollableHeight();
    return h <= 0 ? 0 : currentY() / h;
  }

  /* -- 1) Header: calm directional hide ------------------------------------ */
  var HIDE_AFTER = 80; // don't hide until at least ~a header height down
  var DELTA = 6;       // ignore sub-pixel jitter
  var lastY = currentY();
  var headerHidden = false;

  function showHeader() {
    if (!header || !headerHidden) return;
    header.classList.remove('dn-calm-hide-up');
    headerHidden = false;
    recomputeContract();
  }
  function hideHeader() {
    if (!header || headerHidden) return;
    if (reducedMotion() || !isMobile() || hasOpenOverlay()) return;
    header.classList.add('dn-calm-hide-up');
    headerHidden = true;
    recomputeContract();
  }
  function updateHeader(y) {
    if (!isMobile() || reducedMotion()) { showHeader(); return; }
    if (y <= HIDE_AFTER || hasOpenOverlay()) { showHeader(); return; }
    if (y > lastY + DELTA) hideHeader();
    else if (y < lastY - DELTA) showHeader();
  }

  /* -- 3) Back-to-top: only past ~50% -------------------------------------- */
  function updateScrollTop(depth) {
    body.classList.toggle('dn-scrolltop-armed', isMobile() && depth >= 0.5);
  }

  /* -- 4) Chat launcher nudge ---------------------------------------------- */
  var DWELL_MS = 24000; // long idle dwell before a gentle nudge
  var DEEP = 0.6;       // "deep scroll" nudge threshold
  var dwellTimer = null;
  var launcher = null;

  function findLauncher() {
    if (!launcher || !document.contains(launcher)) {
      launcher = document.querySelector('.widget-button');
    }
    return launcher;
  }
  function nudgeOn() {
    if (!isMobile() || body.classList.contains('dn-chat-open')) return;
    body.classList.add('dn-chat-nudge');
  }
  function nudgeOff() {
    body.classList.remove('dn-chat-nudge');
  }
  function armDwell() {
    if (dwellTimer) window.clearTimeout(dwellTimer);
    dwellTimer = window.setTimeout(nudgeOn, DWELL_MS);
  }
  function onActivity() {
    armDwell();
    // An interaction dismisses an idle nudge, but a deep-scroll nudge persists
    // on its own (the scroll handler owns that state).
    if (scrollDepth() < DEEP) nudgeOff();
  }

  /* -- Unified scroll handler (rAF-throttled) ------------------------------ */
  var ticking = false;
  function onScroll() {
    armDwell(); // scrolling counts as activity → resets the idle clock
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      var y = currentY();
      var depth = scrollDepth();
      updateHeader(y);
      updateScrollTop(depth);
      if (isMobile() && depth >= DEEP) nudgeOn();
      else if (depth < DEEP) nudgeOff();
      lastY = y;
      ticking = false;
    });
  }

  /* -- 2) Filter trigger only over the grid -------------------------------- */
  var gridObserver = null;
  function setupGridObserver() {
    if (gridObserver) return;
    if (!('IntersectionObserver' in window)) return;
    var grid = document.querySelector('[data-dn-filter-grid]');
    if (!grid) return; // no grid (non-collection page) → no-op
    gridObserver = new IntersectionObserver(function (entries) {
      var inView = entries.some(function (e) { return e.isIntersecting; });
      body.classList.toggle('dn-calm-filter-offgrid', !inView);
    }, { root: null, threshold: 0 });
    gridObserver.observe(grid);
  }

  /* -- Wiring -------------------------------------------------------------- */
  window.addEventListener('scroll', onScroll, { passive: true });

  window.addEventListener('resize', function () {
    if (!isMobile()) {
      showHeader();
      body.classList.remove('dn-scrolltop-armed', 'dn-calm-filter-offgrid', 'dn-chat-nudge');
    }
    recomputeContract();
  }, { passive: true });

  ['pointerdown', 'touchstart', 'keydown'].forEach(function (evt) {
    window.addEventListener(evt, onActivity, { passive: true });
  });

  // Reveal the header whenever a drawer / modal opens so it is never left
  // hidden behind an overlay.
  document.addEventListener('shown.bs.offcanvas', showHeader);
  document.addEventListener('shown.bs.modal', showHeader);

  // The visitor noticing the launcher (tap / focus) ends the nudge.
  document.addEventListener('pointerdown', function (e) {
    var l = findLauncher();
    if (l && e.target instanceof Node && l.contains(e.target)) nudgeOff();
  }, { passive: true });
  document.addEventListener('focusin', function (e) {
    var l = findLauncher();
    if (l && e.target instanceof Node && l.contains(e.target)) nudgeOff();
  });

  /* -- Boot ---------------------------------------------------------------- */
  function boot() {
    lastY = currentY();
    setupGridObserver();
    updateScrollTop(scrollDepth());
    armDwell();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  // The filter app renders its grid asynchronously — retry the observer setup.
  window.setTimeout(setupGridObserver, 1200);
  window.setTimeout(setupGridObserver, 3000);
})();
